import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Package, Pencil, Save, Lock, Boxes } from 'lucide-react';

/**
 * Plans Manager card for the Super Admin console.
 * Lets the platform owner edit price/max users/storage/modules per plan.
 */
export default function PlansManagerCard({ onChanged }) {
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, m] = await Promise.all([
        api.get('/super-admin/plans'),
        api.get('/super-admin/plans/available-modules').catch(() => ({ data: { modules: [] } })),
      ]);
      setPlans((p.data || []).filter(x => !x.is_custom));
      setModules(m.data?.modules || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/super-admin/plans/${editing.id}`, {
        name: editing.name,
        price_inr: parseInt(editing.price_inr, 10) || 0,
        max_users: editing.max_users === '' || editing.max_users === null ? null : parseInt(editing.max_users, 10),
        storage_gb: parseInt(editing.storage_gb, 10) || 0,
        modules: editing.modules || [],
      });
      toast.success(`${editing.name} updated — applies to all tenants on this plan.`);
      setEditing(null);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="plans-manager">
      <div className="flex items-center gap-2 mb-3">
        <Package className="w-4 h-4 text-amber-700" />
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Plans manager</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {plans.map(p => (
          <div key={p.id} className="border border-slate-200 rounded-xl p-3 hover:border-amber-300 transition-colors" data-testid={`plan-card-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-bold text-slate-900">{p.name}</div>
              <Button size="sm" variant="ghost" onClick={() => setEditing({ ...p })}
                      data-testid={`edit-plan-${p.id}`} className="h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-1 tabular-nums">
              ₹{(p.price_inr || 0).toLocaleString('en-IN')}<span className="text-xs text-slate-400 font-normal">/mo</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {p.max_users ? <><b>{p.max_users}</b> users</> : <><Boxes className="w-3 h-3 inline" /> Unlimited</>}
              {' · '}{p.storage_gb} GB
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(p.modules || []).map(m => (
                <span key={m} className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{m}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-plan-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Edit {editing?.name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Plan name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-lg mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Price /mo (₹)</Label>
                  <Input type="number" value={editing.price_inr} onChange={(e) => setEditing({ ...editing, price_inr: e.target.value })} data-testid="edit-price" className="rounded-lg mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Max users <span className="text-[10px] text-slate-400">(blank = ∞)</span></Label>
                  <Input type="number" value={editing.max_users || ''} onChange={(e) => setEditing({ ...editing, max_users: e.target.value })} data-testid="edit-users" className="rounded-lg mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Storage (GB)</Label>
                  <Input type="number" value={editing.storage_gb} onChange={(e) => setEditing({ ...editing, storage_gb: e.target.value })} data-testid="edit-storage" className="rounded-lg mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Modules included</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {modules.map(m => {
                    const on = (editing.modules || []).includes(m);
                    const toggle = () => {
                      const cur = editing.modules || [];
                      setEditing({ ...editing, modules: on ? cur.filter(x => x !== m) : [...cur, m] });
                    };
                    return (
                      <button type="button" key={m} onClick={toggle}
                              data-testid={`edit-mod-${m}`}
                              className={`text-xs px-2.5 py-1 rounded-full border-2 transition-colors ${on ? 'border-amber-500 bg-amber-100 text-amber-800 font-semibold' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="save-plan"
                    className="rounded-full bg-gradient-to-r from-amber-500 to-rose-500 text-white">
              <Save className="w-4 h-4 mr-1.5" /> {busy ? 'Saving…' : 'Save plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
